"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Clock, ShieldCheck, Wrench, CircleCheck as CheckCircle2, ArrowRight, Droplets } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const keyBenefits = [{
  text: "Fast arrival across Jonesboro & Clayton County"
}, {
  text: "Upfront pricing with zero hidden fees"
}, {
  text: "Licensed, bonded, and insured technicians"
}];
export default function FaucetSinkHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faucetsinkrepairhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Decorative subtle background gradient */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--secondary)/0.5),transparent_60%)]" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Core Value Proposition */}
          <div className="lg:col-span-7 space-y-6">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase">
                <Clock className="size-3.5 text-primary" />
                24/7 Emergency Dispatch
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs font-medium">
                <Droplets className="size-3.5 text-accent-foreground" />
                Jonesboro, GA Local Specialists
              </Badge>
            </motion.div>

            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="space-y-4">
              <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
                Reliable Faucet & Sink Repair in Jonesboro, GA
              </h1>
              <p className="max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                Dripping faucets, broken garbage disposals, corroded sink pipes, or low water pressure? Jonesboro Emergency Plumber delivers prompt diagnosis, leak repair, and precision installation for residential and commercial fixtures.
              </p>
            </motion.div>

            {/* Benefit Checklist */}
            <motion.ul initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="space-y-2.5 pt-2">
              {keyBenefits.map((benefit, i) => <li key={benefit.text} data-index={i} className="flex items-center gap-2.5 text-sm sm:text-base text-foreground font-medium">
                  <CheckCircle2 className="size-5 text-accent-foreground shrink-0" />
                  <span>{benefit.text}</span>
                </li>)}
            </motion.ul>

            {/* Action Buttons */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-md transition-colors">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-secondary font-medium transition-colors">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <span>Schedule Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Trust Footer line */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="flex items-center gap-4 pt-2 text-xs text-muted-foreground border-t border-border/60">
              <span className="flex items-center gap-1">
                <ShieldCheck className="size-4 text-primary" />
                100% Workmanship Guarantee
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Wrench className="size-4 text-primary" />
                Same-Day Repair Available
              </span>
            </motion.div>
          </div>

          {/* Right Column: Hero Media & Support Cards */}
          <div className="lg:col-span-5 relative">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative mx-auto max-w-md lg:max-w-none">
              {/* Main Image Card */}
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <AspectRatio ratio={4 / 3}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/faucet_sink_repair_service.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/faucet_sink_repair_service.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/faucet_sink_repair_service.webp-wvc-sizes" alt="Plumber fixing kitchen sink plumbing pipes under counter" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                </AspectRatio>
              </div>

              {/* Floating Stat Badge 1 */}
              <motion.div initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="absolute -bottom-6 -left-4 sm:-left-6">
                <Card className="border-border/80 bg-card/95 backdrop-blur-sm shadow-lg p-3 sm:p-4 rounded-xl max-w-[220px]">
                  <CardContent className="p-0 flex items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      <Clock className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Response Time</p>
                      <p className="text-sm font-bold text-foreground">Under 45 Mins</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Floating Stat Badge 2 */}
              <motion.div initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="absolute -top-4 -right-2 sm:-right-4">
                <Card className="border-border/80 bg-accent text-accent-foreground shadow-md p-2.5 sm:p-3 rounded-lg">
                  <CardContent className="p-0 flex items-center gap-2">
                    <ShieldCheck className="size-4 shrink-0" />
                    <span className="text-xs font-bold">Licensed & Insured</span>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}